<script lang="ts">
    import {feedback, meetings, requests, user} from '../stores';
    import MemberComponent from '../components/Member.svelte';
    import {Button, Card, CardActions, CardSubtitle, CardText, CardTitle, Dialog} from 'svelte-materialify';
    import {Icon, Select, Table, TextField, Checkbox} from 'svelte-materialify/src';
    import type {Meeting, Member, Request} from '../firestore';
    import {acceptRequest, addRequest, denyRequest, tryDo, removeRequest} from '../firestore';
    import type {User} from 'firebase';
    import {dateIso, getItemById, isUserAdmin, meetingSeatAssignments, externalSeat} from '../util';
    import {kSeats, kTeams, kRooms} from '../rules';
    import type {RoomName} from '../rules';
    import SeatPicker from "../components/SeatPicker.svelte";

    let showNewRequest = false;
    $: nrMeetingAvailability = nrMeetings($meetings, $requests, $user);
    $: meetingOptions = nrAvailable(nrMeetingAvailability);

    let selectedTeam: string|null|[] = null;
    let resolved = [];
    // svelte-materialify 0.3.3 bug?
    // $: if (selectedTeam === null) selectedTeam = [];

    let nrMeeting: string|null|[] = [], nrGoal: string = '';
    // $: if (nrMeeting === null) nrMeeting = [];
    function validateRequest(meeting, goal) {
        if (meeting?.length === 0) return "Please pick a meeting";
        if (Array.from(goal).filter(it => it.trim().length > 0).length <= 10) return "Your goal needs more information";
        for (const banned of ['owo', 'uwu', '0w0', 'o w o', 'u w u']) {
            if (nrGoal.toLowerCase().includes(banned)) return "delet this";
        }
        return "";
    }
    $: nrHint = validateRequest(nrMeeting, nrGoal);
    $: nrCanSubmit = nrHint.length === 0;

    function requestsToShow(allRequests: Request[], selectedTeam: string|[], meetings: Meeting[], showResolved: boolean): Request[] {
        const collection = selectedTeam == null ? allRequests : allRequests.filter(it => it.author.team === selectedTeam);
        const notOutdated = collection.filter(it => getItemById(meetings, it.meeting)?.endTime?.getTime() ?? 0 > Date.now());
        const requestsWithResolved = notOutdated.sort((b, a) => a.created.getTime() - b.created.getTime());
        if (showResolved) {
            return requestsWithResolved;
        } else {
            return requestsWithResolved.filter(it => it.accepted === undefined);
        }
    }

    function newRequest() {
        if (meetingOptions.length === 0) {
            const explanation = Object.keys(nrMeetingAvailability).map(it => `${it}: ${nrMeetingAvailability[it]}`).join('\n');
            alert("Sorry, no more meetings to request to attend\n\n" + explanation);
        } else {
            nrMeeting = undefined;
            nrGoal = '';
            showNewRequest = true;
        }
    }

    function nrMeetings(meetings: Meeting[], requests: Request[], user: User): { [id: string]: string | null } {
        return Object.fromEntries(meetings.map(meeting => {
            if (meeting.attendees.find(it => it.identity.email === user.email) !== undefined) {
                return [meeting.id, 'Already attending'];
            }
            if (requests.find(it => it.author.email === user.email && it.meeting === meeting.id) !== undefined) {
                return [meeting.id, 'Already requested'];
            }
            if (meeting.attendees.length >= meeting.capacity) {
                return [meeting.id, 'Full'];
            }
            if (meeting.startTime.getTime() <= new Date().getTime()) {
                if (meeting.endTime.getTime() <= new Date().getTime()) {
                    return [meeting.id, 'Already ended'];
                } else {
                    return [meeting.id, 'Already started'];
                }
            }
            return [meeting.id, null];
        }));
    }

    function nrAvailable(meetings: { [id: string]: string | null }): string[] {
        return Object.keys(meetings).filter(id => meetings[id] === null);
    }

    function submitNewRequest() {
        tryDo(() => addRequest(nrMeeting, nrGoal));
        showNewRequest = false;
    }

    let showAcceptRequest = false;
    let arRequest: Request | null = null;
    $: arMeeting = arRequest == null ? null : getItemById($meetings, arRequest.meeting);
    let arSeat: string | null = null;
    let arGoal: string | null = null;
    let arRoom: RoomName = kRooms[0];

    function arMustAssignExternalSeat(meet: Meeting): boolean {
        const emptyEngSeats = kSeats.filter(seat => meet.attendees.every(attendee => attendee.seat !== seat));
        return meet.attendees.length < meet.capacity && emptyEngSeats.length === 0;
    }

    function arValidate(meet: Meeting, seat: string, goal: string, room: RoomName): string|undefined {
        if (meet.attendees.length >= meet.capacity) {
            return 'The meeting is full';
        } else if (arMustAssignExternalSeat(meet) && room === 'Engineering Room') {
            return 'The Engineering Room is full';
        } else if (goal.trim().length < 10) {
            return 'The goal needs more information';
        }
    }

    function beginAcceptRequest(request: Request) {
        arRequest = request;
        arGoal = request.goal;
        showAcceptRequest = true;
    }

    async function finishAcceptRequest() {
        if (arRoom !== 'Engineering Room') {
            arSeat = externalSeat(arMeeting, arRoom);
        }
        showAcceptRequest = false;
        await tryDo(() => acceptRequest(arRequest.id, arSeat, arGoal));
        feedback.set(`Accepted ${arRequest.author.name}'s request to attend ${arRequest.meeting}`);
        arSeat = null;
    }

</script>

<div class="requests">
    <div class="add-request-container">
        <Button size="large" rounded class="primary-color" on:click={newRequest}>
            <Icon class="material-icons">add</Icon>
            &nbsp;
            New Request
        </Button>
        <div class="d-flex">
            <div class="mr-4 d-flex">
                <Checkbox bind:group={resolved} value="showResolved">Show resolved</Checkbox>
            </div>
            <Select solo items={kTeams.map(t => ({name: t, value: t}))}
                    bind:value={selectedTeam} placeholder="Team">
            </Select>
        </div>
    </div>
    <Table dense>
        <thead>
            <tr>
                <th>Date</th>
                <th>Creator</th>
                <th>Meeting</th>
                <th>Goal</th>
                <th>Status</th>
                <th></th>
            </tr>
        </thead>
        <tbody>
            {#each requestsToShow($requests, selectedTeam, $meetings, resolved.length !== 0) as req (req.id)}
                <tr>
                    <td>{dateIso(req.created)}</td>
                    <td><MemberComponent value={req.author} /></td>
                    <td>{req.meeting}</td>
                    <td>{req.goal}</td>
                    <td>
                        {#if isUserAdmin($user)}
                            <Button icon disabled={req.accepted === true ? true : undefined} class="{req.accepted === true ? 'grey lighten-2' : ''}"
                                    on:click={() => beginAcceptRequest(req)}>
                                <span class="material-icons green-text" >check</span>
                            </Button>
                            <Button icon disabled={req.accepted === false ? true : undefined} class="{req.accepted === false ? 'grey lighten-2' : ''}"
                                    on:click={() => tryDo(() => denyRequest(req.id))
                                        .then(() => feedback.set(`Rejected ${req.author.name}'s request to attend ${req.meeting}`))}>
                                <span class="material-icons red-text">close</span>
                            </Button>
                        {:else}
                            {#if req.accepted === true}
                                <Icon class="material-icons">check</Icon> Approved
                            {:else if req.accepted === false}
                                <Icon class="material-icons">close</Icon> Rejected
                            {:else}
                                <Icon class="material-icons">hourglass_bottom</Icon> Pending
                            {/if}
                        {/if}
                    </td>
                    <td>
                        {#if req.author.email === $user.email || isUserAdmin($user)}
                            <Button icon class="red-text" on:click={() => removeRequest(req.id)}>
                                <span class="material-icons">delete</span>
                            </Button>
                        {/if}
                    </td>
                </tr>
            {/each}
        </tbody>
    </Table>
</div>

<Dialog bind:active={showNewRequest}>
    <Card>
        <CardTitle>New Request</CardTitle>
        <CardText>
            <div class="form-fields">
                <Select class="pa-3" items={meetingOptions.map(it => ({name: it, value: it}))}
                    bind:value={nrMeeting}>Meeting</Select>
                <TextField class="pa-3" placeholder="What will you achieve?" bind:value={nrGoal} hint={nrHint}>Goal</TextField>
            </div>
        </CardText>
        <CardActions>
            <Button text class="{nrCanSubmit ? 'primary-text' : 'grey-text'}"
                disabled={nrCanSubmit ? undefined : true}
                on:click={submitNewRequest}>
                <Icon class="material-icons">send</Icon>&nbsp;
                Submit
            </Button>
            <Button text class="secondary-text" on:click={() => showNewRequest = false}>
                <Icon class="material-icons">delete</Icon>&nbsp;
                Discard
            </Button>
        </CardActions>
    </Card>
</Dialog>

<Dialog bind:active={showAcceptRequest} fullscreen class="overflow-visible">
    {#if arRequest != null}
    <Card>
        <CardTitle>Review Request</CardTitle>
        <CardSubtitle>{arRequest.author.name} wants to attend {arRequest.meeting}</CardSubtitle>
        <CardText>
            {#if arMeeting.attendees.length >= arMeeting.capacity}
                This meeting is full.
            {:else if arMustAssignExternalSeat(arMeeting) && arRoom === 'Engineering Room'}
                No empty seats remain inside the Engineering Room. Select another location.
            {:else if arRoom !== 'Engineering Room'}
                {arRequest.author.name} must remain at the {arRoom}. Their nominal seat is {externalSeat(arMeeting, arRoom)}.
            {:else}
            <div class="d-flex" style="justify-content: center">
                <SeatPicker assignments={meetingSeatAssignments(arMeeting)} bind:selected={arSeat}/>
            </div>
            {/if}
            <Select mandatory items={kRooms.map(i => ({name: i, value: i}))} placeholder="Room" bind:value={arRoom} class="pa-3">Location</Select>
            <TextField class="pa-3" placeholder="What will they achieve?" bind:value={arGoal}>Goal</TextField>
        </CardText>
        <CardActions>
            <Button text class="{(arSeat === null || arGoal.trim().length < 10) ? '' : 'primary-text'}"
                disabled={arValidate(arMeeting, arSeat, arGoal, arRoom) !== undefined ? true : undefined}
                on:click={finishAcceptRequest}>
                <Icon class="material-icons">send</Icon>&nbsp;
                Submit
            </Button>
            <Button text class="secondary-text" on:click={() => { arSeat = null; showAcceptRequest = false; }}>
                <Icon class="material-icons">delete</Icon>&nbsp;
                Discard
            </Button>
        </CardActions>
    </Card>
    {/if}
</Dialog>

<style>
    .requests {
        max-width: 1200px;
        padding: 1rem;
        margin: 0 auto;
    }
    .add-request-container {
        margin-bottom: 1rem;
        display: flex;
        justify-content: space-between;
    }
</style>